/**
 * Read-only vertical timeline visualization of a playbook's action sequence.
 * Used on PlaybookDetail (not the builder — that's action-builder.tsx).
 */
import { ACTION_META, type PlaybookActionType } from './action-type-meta';
import { EdgeActionWarning } from './EdgeActionWarning';
import type { PlaybookAction } from '@/api/healthcheck';
import { fmtSeconds } from '../shared';

interface Props {
  actions: PlaybookAction[];
}

export function PlaybookSequence({ actions }: Props) {
  if (!actions.length) {
    return (
      <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
        No actions configured.
      </p>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {actions.map((action, i) => {
        const meta = ACTION_META[action.type as PlaybookActionType];
        const Icon = meta?.icon;
        const isFirst = i === 0;

        return (
          <div key={i}>
            {/* Wait gap between steps */}
            {!isFirst && action.delay > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--vn-space-2)',
                  padding: 'var(--vn-space-1) 0 var(--vn-space-1) var(--vn-space-5)',
                  color: 'var(--vn-fg-subtle)',
                  fontSize: 'var(--vn-font-size-xs)',
                }}
              >
                <div
                  style={{
                    width: 1,
                    height: 16,
                    background: 'var(--vn-border-default)',
                    marginRight: 'var(--vn-space-1)',
                  }}
                />
                wait {fmtSeconds(action.delay)}
              </div>
            )}
            {!isFirst && action.delay === 0 && (
              <div style={{ width: 1, height: 8, background: 'var(--vn-border-default)', marginLeft: 'var(--vn-space-5)', marginBottom: 'var(--vn-space-1)' }} />
            )}

            {/* Action card */}
            <div style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'flex-start' }}>
              {/* Node */}
              <div
                style={{
                  marginTop: 'var(--vn-space-1)',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: meta?.destructive ? 'var(--vn-danger-solid)' : 'var(--vn-accent-solid)',
                  border: '2px solid var(--vn-border-strong)',
                  flexShrink: 0,
                }}
              />

              <div
                style={{
                  flex: 1,
                  border: '1px solid var(--vn-border-default)',
                  borderRadius: 'var(--vn-radius-md)',
                  padding: 'var(--vn-space-3)',
                  background: 'var(--vn-bg-surface)',
                }}
              >
                {/* Action header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-2)' }}>
                  {Icon && <Icon size={15} style={{ color: meta?.destructive ? 'var(--vn-danger-text)' : 'var(--vn-fg-muted)' }} />}
                  <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], fontSize: 'var(--vn-font-size-sm)' }}>
                    {isFirst ? '1 - immediate' : `${i + 1}`} — {meta?.label ?? action.type}
                  </span>
                </div>

                {/* Destructive warning */}
                {meta?.destructive && meta.warning && (
                  <div style={{ marginBottom: 'var(--vn-space-2)' }}>
                    <EdgeActionWarning warning={meta.warning} />
                  </div>
                )}

                {/* Config summary */}
                <dl
                  style={{
                    margin: 0,
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: 'var(--vn-space-1) var(--vn-space-3)',
                    fontSize: 'var(--vn-font-size-sm)',
                  }}
                >
                  {Object.entries(action.config).map(([k, v]) => {
                    if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return null;
                    let display: string;
                    if (Array.isArray(v)) {
                      display = `${(v as unknown[]).length} item${(v as unknown[]).length === 1 ? '' : 's'}`;
                    } else if (v !== null && typeof v === 'object') {
                      const count = Object.keys(v as object).length;
                      display = count === 0 ? '(none)' : `${count} header${count === 1 ? '' : 's'}`;
                    } else {
                      display = String(v);
                    }
                    return (
                      <div key={k} style={{ display: 'contents' }}>
                        <dt style={{ color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>{k}</dt>
                        <dd style={{ margin: 0, fontFamily: 'var(--vn-font-mono)' }}>
                          {display}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
